import React, { useEffect, useState } from 'react';
import { Container, Header, Card, Label, Breadcrumb } from 'semantic-ui-react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { getTranscriptsForModel, formatUrlPart, getTranscriptId, SITE_BASE_URL } from '../services/api';
import TopSection from './TopSection';
import DisclaimerLink from './DisclaimerLink';
import { toTitleCase } from '../utils/format';
import '../index.css';

const ModelPage = () => {
  const { brand, model } = useParams();
  const [transcripts, setTranscripts] = useState([]);
  const [displayBrand, setDisplayBrand] = useState('');
  const [displayModel, setDisplayModel] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    getTranscriptsForModel(brand, model)
      .then(data => {
        setTranscripts(data);
        if (data.length > 0) {
          const firstTranscript = data[0];
          setDisplayBrand(firstTranscript.details.vehicle.make);
          setDisplayModel(firstTranscript.details.vehicle.model);
        } else {
          setDisplayBrand(toTitleCase(brand));
          setDisplayModel(toTitleCase(model));
        }
      })
      .catch(err => console.error(err));
  }, [brand, model]);

  const handleSearch = () => {
    if (searchQuery.trim() !== '') {
      navigate(`/search/${formatUrlPart(searchQuery)}`);
    }
  };

  const baseUrl = SITE_BASE_URL.replace(/\/$/, '');
  const canonicalUrl = `${baseUrl}/brand/${formatUrlPart(brand)}/model/${formatUrlPart(model)}`;

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": baseUrl },
      { "@type": "ListItem", "position": 2, "name": displayBrand, "item": `${baseUrl}/brand/${formatUrlPart(brand)}` },
      { "@type": "ListItem", "position": 3, "name": displayModel, "item": canonicalUrl }
    ]
  };

  const title = `${displayBrand} ${displayModel} - RV Support & Troubleshooting`;
  const description = `Step-by-step ${displayBrand} ${displayModel} troubleshooting from resolved support calls. Fix common RV issues with certified technician transcripts.`;

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={`${baseUrl}/bcrvlogo.png`} />
        <meta property="og:site_name" content="Blue Compass RV Technical Support" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>

      <TopSection
        title={`${displayBrand} ${displayModel} Customer Support`}
        showLogo={false}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        handleSearch={handleSearch}
      />
      <main>
      <Container className="container" style={{ padding: '20px', backgroundColor: '#ffffff' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }}>
          <Breadcrumb style={{ margin: 0 }}>
            <Breadcrumb.Section link onClick={() => navigate('/')}>
              Home
            </Breadcrumb.Section>
            <Breadcrumb.Divider icon="right angle" />
            <Breadcrumb.Section link onClick={() => navigate(`/brand/${formatUrlPart(brand)}`)}>
              {displayBrand}
            </Breadcrumb.Section>
            <Breadcrumb.Divider icon="right angle" />
            <Breadcrumb.Section active>
              {displayModel}
            </Breadcrumb.Section>
          </Breadcrumb>
          <DisclaimerLink />
        </div>

        <Header
          as="h2"
          textAlign="center"
          style={{
            marginTop: '40px',
            marginBottom: '30px',
            color: '#000',
            fontWeight: 'bold',
            fontSize: '2.5em',
          }}
        >
          Articles
        </Header>
        <Card.Group itemsPerRow={1} stackable style={{ margin: '0 -10px' }}>
          {transcripts.map(call => {
            const id = getTranscriptId(call);
            if (!id) return null;
            return (
            <Card
              key={id}
              href={`${process.env.PUBLIC_URL || ''}/brand/${formatUrlPart(brand)}/model/${formatUrlPart(model)}/${id}`}
              raised
              className="custom-card"
              style={{
                margin: '10px',
                backgroundColor: '#f9f9f9',
                border: '1px solid #ddd',
              }}
            >
              <Card.Content>
                <Card.Header style={{ color: '#007bff' }}>
                  {call.details && call.details.problems && call.details.problems.length > 0
                    ? call.details.problems[0]
                    : 'Call Transcript'}
                </Card.Header>
                <Card.Meta style={{ color: '#555' }}>
                  {call.details && call.details.resolvedOnCall ? 'Resolved on Call' : 'Unresolved'}
                </Card.Meta>
                <Card.Description>
                  <strong>Problems:</strong>
                  <Label.Group>
                    {call.details && call.details.problems && call.details.problems.length > 0
                      ? call.details.problems.map((problem, idx) => (
                          <Label basic color="blue" key={idx}>
                            {problem}
                          </Label>
                        ))
                      : <span>No problems listed</span>}
                  </Label.Group>
                </Card.Description>
              </Card.Content>
            </Card>
            );
          })}
        </Card.Group>
      </Container>
      </main>
    </>
  );
};

export default ModelPage;